import {
  getEmployeeName,
  isAEMT,
  isAPRN,
  isCartEligible,
  isBikeEligible,
  isDirector,
  isDispatcher,
  isEMR,
  isEMT,
  isManager,
  isPA,
  isParamedic,
  isPhysician,
  isResident,
  isRN,
  isSupervisor,
  isTech,
  isTentEligible,
  isTransportEligible,
} from "./scheduleEmployeeUtils";
import { normalizeDay, uid, ASSIGNMENTS_FIELD } from "./scheduleDayUtils";
import {
  getSlotTemplateRange,
  overlaps,
  timeToMinutes,
} from "./scheduleTimeUtils";
import {
  isAmbulanceSlot,
  isBikeSlot,
  isCartSlot,
  isDispatcherSlot,
  isOpsSupervisor,
  isUnlimitedPeopleSlot,
} from "./scheduleRoleUtils";

/* ---------------- cart / bike child-slot helpers ---------------- */
function getCartBikeChildType(roleKey = "") {
  const key = String(roleKey || "").trim().toLowerCase();
  if (key.endsWith("__paramedic")) return "paramedic";
  if (key.endsWith("__partner")) return "partner";
  return "";
}

function getCartBikeBaseRoleKey(roleKey = "") {
  const key = String(roleKey || "");
  return key.replace(/__(paramedic|partner)$/i, "");
}

function isCartBikeChildRoleKey(roleKey = "") {
  return /__(paramedic|partner)$/i.test(String(roleKey || "").trim());
}

function normalizeNamedRole(roleLabel = "", roleKey = "") {
  const rawLabel = String(roleLabel || "").trim();
  const rawKey = String(roleKey || "").trim();
  const source = rawLabel || rawKey;

  if (/^Physician(?:\s+|__)\d+$/i.test(source)) return "Physician";
  if (/^APRN\/PA(?:\s+|__)\d+$/i.test(source)) return "APRN/PA";
  if (/^RN(?:\s+|__)\d+$/i.test(source)) return "RN";
  if (/^Paramedic(?:\s+|__)\d+$/i.test(source)) return "Paramedic";
  if (/^EMT\/AEMT(?:\s+|__)\d+$/i.test(source)) return "EMT/AEMT";
  if (/^Ambulance(?:\s+|__)\d+$/i.test(source)) return "Ambulance";
  if (/^Ambulance\s*\(ALS\)(?:\s+|__)\d+$/i.test(source)) return "Ambulance";
  if (/^Ambulance\s*\(BLS\)(?:\s+|__)\d+$/i.test(source)) return "Ambulance";

  const tentDisplayMatch = rawLabel.match(/^(Tent\s+\d+)\s*-\s*Slot\s+\d+/i);
  if (tentDisplayMatch) return tentDisplayMatch[1];

  const tentKeyMatch = rawKey.match(/^(Tent\s+\d+)__(\d+)$/i);
  if (tentKeyMatch) return tentKeyMatch[1];

  return source;
}

function getEffectiveTemplateRole(roleKey, roleLabel) {
  if (isCartBikeChildRoleKey(roleKey)) {
    return getCartBikeBaseRoleKey(roleKey);
  }
  return normalizeNamedRole(roleLabel, roleKey);
}

function isCartBikeContext(roleKey, roleLabel) {
  if (isCartBikeChildRoleKey(roleKey)) return true;
  const effectiveRole = getEffectiveTemplateRole(roleKey, roleLabel);
  return isCartSlot(effectiveRole) || isBikeSlot(effectiveRole);
}

/* ---------------- qualifications ---------------- */
export function isQualifiedForRole(emp, roleLabel, roleKey = "") {
  if (!emp) return false;

  const normalizedRole = normalizeNamedRole(roleLabel, roleKey);

  if (isAmbulanceSlot(normalizedRole)) return false;

  const childType = getCartBikeChildType(roleKey);
  const effectiveRole = getEffectiveTemplateRole(roleKey, normalizedRole);

  if (childType === "paramedic") {
    if (isBikeSlot(effectiveRole)) {
      return isBikeEligible(emp) && isParamedic(emp);
    }
    return isParamedic(emp);
  }

  if (childType === "partner") {
    if (isBikeSlot(effectiveRole)) {
      return isBikeEligible(emp);
    }
    if (isCartSlot(effectiveRole)) {
      return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
    }
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  if (/^Tent\s+\d+$/i.test(String(normalizedRole || "").trim())) return isTentEligible(emp);

  if (isBikeSlot(normalizedRole)) return isBikeEligible(emp);
  if (isCartSlot(normalizedRole)) return isCartEligible(emp);

  if (normalizedRole === "Incident Command") return isDirector(emp);
  if (normalizedRole === "Planning") return isDirector(emp);
  if (normalizedRole === "Logistics") return isDirector(emp);
  if (normalizedRole === "Unified Command") return isDirector(emp) || isManager(emp) || isSupervisor(emp);
  if (normalizedRole === "Ops Manager") return isDirector(emp) || isManager(emp);

  if (String(normalizedRole || "").startsWith("Dispatcher")) return isDispatcher(emp) || isSupervisor(emp);
  if (normalizedRole === "Triage Supervisor") return isSupervisor(emp);
  if (isOpsSupervisor(normalizedRole)) return isSupervisor(emp);

  if (normalizedRole === "Physician") return isPhysician(emp);
  if (normalizedRole === "APRN/PA") return isAPRN(emp) || isPA(emp);
  if (normalizedRole === "RN") return isRN(emp);
  if (normalizedRole === "Paramedic") return isParamedic(emp);
  if (normalizedRole === "EMT/AEMT") return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  if (normalizedRole === "Transport") return isTransportEligible(emp);

  if (normalizedRole === "Director") return isDirector(emp);
  if (normalizedRole === "Manager") return isManager(emp);
  if (normalizedRole === "Supervisor") return isSupervisor(emp);
  if (normalizedRole === "APRN") return isAPRN(emp);
  if (normalizedRole === "PA") return isPA(emp);
  if (normalizedRole === "AEMT") return isAEMT(emp);
  if (normalizedRole === "EMT") return isEMT(emp);
  if (normalizedRole === "EMR") return isEMR(emp);
  if (normalizedRole === "Tech") return isTech(emp);
  if (normalizedRole === "Resident") return isResident(emp);

  return false;
}

export function isQualifiedForPublishedPosition(emp, position, roleKey = "") {
  const p = normalizeNamedRole(position, roleKey);
  if (!p) return false;

  const childType = getCartBikeChildType(roleKey);
  const effectiveRole = getEffectiveTemplateRole(roleKey, p);

  if (childType === "paramedic") {
    if (isBikeSlot(effectiveRole)) {
      return isBikeEligible(emp) && isParamedic(emp);
    }
    return isParamedic(emp);
  }

  if (childType === "partner") {
    if (isBikeSlot(effectiveRole)) {
      return isBikeEligible(emp);
    }
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  if (p === "EMT/Paramedic") return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  if (p === "EMT/AEMT") return isEMT(emp) || isAEMT(emp) || isParamedic(emp);

  return isQualifiedForRole(emp, p, roleKey);
}

/* ---------------- assignment lookups ---------------- */
export function getEmployeeAssignmentsForDay(day, employeeId, exclude = null) {
  const assignments = normalizeDay(day)[ASSIGNMENTS_FIELD] || {};
  const out = [];

  for (const [roleKey, list] of Object.entries(assignments)) {
    if (isAmbulanceSlot(roleKey)) continue;
    for (const a of list || []) {
      if (a.employeeId !== employeeId) continue;
      if (exclude && exclude.assignmentId && exclude.assignmentId === a.assignmentId) continue;
      out.push({ ...a, roleKey });
    }
  }

  return out;
}

export function hasEmployeeOverlap(day, employeeId, start, end, exclude = null) {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);
  if (startMin == null || endMin == null || startMin >= endMin) return true;

  const existing = getEmployeeAssignmentsForDay(day, employeeId, exclude);
  return existing.some((a) => {
    const s = timeToMinutes(a.start);
    const e = timeToMinutes(a.end);
    return s != null && e != null && overlaps(startMin, endMin, s, e);
  });
}

/* ---------------- coverage validation ---------------- */
export function validateCartBikeCoverage(assignments, candidate, excludeAssignmentId = "") {
  const points = new Set();
  const all = [...(assignments || []).filter((a) => a.assignmentId !== excludeAssignmentId), candidate];

  for (const a of all) {
    const s = timeToMinutes(a.start);
    const e = timeToMinutes(a.end);
    if (s == null || e == null || s >= e) continue;
    points.add(s);
    points.add(e);
  }

  const sortedPts = [...points].sort((a, b) => a - b);
  if (sortedPts.length < 2) return { ok: true };

  for (let i = 0; i < sortedPts.length - 1; i += 1) {
    const segStart = sortedPts[i];
    const segEnd = sortedPts[i + 1];
    if (segStart >= segEnd) continue;

    const active = all.filter((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return s != null && e != null && overlaps(segStart, segEnd, s, e);
    });

    if (active.length > 2) {
      return { ok: false, message: "Cart/Bike slots can only have 2 people at the same time." };
    }
  }

  return { ok: true };
}

export function validateMaxTwoCoverage(
  assignments,
  candidate,
  excludeAssignmentId = "",
  message = "This slot can only have 2 people at the same time."
) {
  const points = new Set();
  const all = [...(assignments || []).filter((a) => a.assignmentId !== excludeAssignmentId), candidate];

  for (const a of all) {
    const s = timeToMinutes(a.start);
    const e = timeToMinutes(a.end);
    if (s == null || e == null || s >= e) continue;
    points.add(s);
    points.add(e);
  }

  const sortedPts = [...points].sort((a, b) => a - b);
  if (sortedPts.length < 2) return { ok: true };

  for (let i = 0; i < sortedPts.length - 1; i += 1) {
    const segStart = sortedPts[i];
    const segEnd = sortedPts[i + 1];
    if (segStart >= segEnd) continue;

    const active = all.filter((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return s != null && e != null && overlaps(segStart, segEnd, s, e);
    });

    if (active.length > 2) {
      return { ok: false, message };
    }
  }

  return { ok: true };
}

/* ---------------- main placement validation ---------------- */
export function validateSlotPlacement({
  day,
  roleKey,
  roleLabel,
  employee,
  employeeById,
  start,
  end,
  excludeAssignmentId = "",
}) {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);

  if (startMin == null || endMin == null || startMin >= endMin) {
    return { ok: false, message: "Start time must be earlier than end time." };
  }

  const effectiveTemplateRole = getEffectiveTemplateRole(roleKey, roleLabel);
  const template = getSlotTemplateRange(effectiveTemplateRole);

  if (startMin < template.startMin || endMin > template.endMin) {
    return {
      ok: false,
      message: `Assignment must stay inside the slot hours (${template.start}–${template.end}).`,
    };
  }

  if (hasEmployeeOverlap(day, employee.id, start, end, { assignmentId: excludeAssignmentId || "" })) {
    return {
      ok: false,
      message: `${getEmployeeName(employee)} is already scheduled somewhere else during that time.`,
    };
  }

  const nDay = normalizeDay(day);

  const slotAssignments = (nDay[ASSIGNMENTS_FIELD]?.[roleKey] || []).filter(
    (a) => a.assignmentId !== excludeAssignmentId
  );

  if (isUnlimitedPeopleSlot(roleLabel)) {
    return { ok: true };
  }

  if (isCartBikeContext(roleKey, roleLabel)) {
    const baseRoleKey = getCartBikeBaseRoleKey(roleKey);
    const combinedAssignments = [
      ...((nDay[ASSIGNMENTS_FIELD]?.[`${baseRoleKey}__paramedic`] || []).filter(
        (a) => a.assignmentId !== excludeAssignmentId
      )),
      ...((nDay[ASSIGNMENTS_FIELD]?.[`${baseRoleKey}__partner`] || []).filter(
        (a) => a.assignmentId !== excludeAssignmentId
      )),
    ];

    const sameRowConflict = slotAssignments.some((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return s != null && e != null && overlaps(startMin, endMin, s, e);
    });

    if (sameRowConflict) {
      return { ok: false, message: "That time range overlaps an existing assignment in this slot." };
    }

    const coverageCheck = validateCartBikeCoverage(
      combinedAssignments,
      {
        assignmentId: excludeAssignmentId || uid("tmp"),
        employeeId: employee.id,
        start,
        end,
      },
      excludeAssignmentId
    );

    if (!coverageCheck.ok) return coverageCheck;

    return { ok: true };
  }

  if (isDispatcherSlot(roleLabel)) {
    const coverageCheck = validateMaxTwoCoverage(
      slotAssignments,
      {
        assignmentId: excludeAssignmentId || uid("tmp"),
        employeeId: employee.id,
        start,
        end,
      },
      excludeAssignmentId,
      "Dispatch can only have 2 people at the same time."
    );
    if (!coverageCheck.ok) return coverageCheck;

    return { ok: true };
  }

  const conflicting = slotAssignments.some((a) => {
    const s = timeToMinutes(a.start);
    const e = timeToMinutes(a.end);
    return s != null && e != null && overlaps(startMin, endMin, s, e);
  });

  if (conflicting) {
    return { ok: false, message: "That time range overlaps an existing assignment in this slot." };
  }

  return { ok: true };
}